import type {
  AdminArticle,
  AdminBureauMember,
  AdminClubPalmares,
  AdminClubTrophy,
  AdminDashboardData,
  AdminEvent,
  AdminGalleryItem,
  AdminChallenge,
  AdminDirection,
  AdminLiveStream,
  AdminLeisureVideo,
  AdminEventParticipant,
  AdminMatch,
  AdminMember,
  AdminMemberImportReport,
  AdminOrder,
  AdminProduct,
  AdminRequest,
  AdminSquad,
  AdminTeam,
  AdminTeamJoinRequest,
  AdminTraining,
  PaginatedResult,
  UploadedMediaAsset
} from "./types";

const DEFAULT_AVATAR =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'><rect width='120' height='120' rx='24' fill='%23E8521A'/><circle cx='60' cy='46' r='22' fill='white' fill-opacity='0.92'/><path d='M24 102c8-20 25-30 36-30s28 10 36 30' fill='white' fill-opacity='0.92'/></svg>";
const DEFAULT_IMAGE =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 400'><rect width='640' height='400' fill='%23F7F7F5'/><rect x='32' y='32' width='576' height='336' rx='32' fill='%23FFF3ED'/><circle cx='190' cy='144' r='44' fill='%23E8521A' fill-opacity='0.25'/><path d='M96 312l118-108 94 82 86-68 150 94' fill='none' stroke='%23E8521A' stroke-width='26' stroke-linecap='round' stroke-linejoin='round'/></svg>";
const ADMIN_MEDIA_UPLOAD_BATCH_SIZE = 10;

function withQuery(path: string, query: Record<string, string | number | undefined>) {
  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const suffix = searchParams.toString();
  return suffix ? `${path}?${suffix}` : path;
}

function mapMemberStatus(status: string): AdminMember["statut"] {
  switch (status) {
    case "ACTIVE":
      return "actif";
    case "SUSPENDED":
      return "suspendu";
    case "REFUSED":
      return "refuse";
    default:
      return "en_attente";
  }
}

function toBackendMemberStatus(status: AdminMember["statut"]) {
  switch (status) {
    case "actif":
      return "ACTIVE";
    case "suspendu":
      return "SUSPENDED";
    case "refuse":
      return "REFUSED";
    default:
      return "PENDING";
  }
}

function mapArticleStatus(status: string): AdminArticle["statut"] {
  switch (status) {
    case "PUBLISHED":
      return "publie";
    case "ARCHIVED":
      return "archive";
    default:
      return "brouillon";
  }
}

function toBackendArticleStatus(status: AdminArticle["statut"]) {
  switch (status) {
    case "publie":
      return "PUBLISHED";
    case "archive":
      return "ARCHIVED";
    default:
      return "DRAFT";
  }
}

function mapEventType(type: string): AdminEvent["type"] {
  switch (type) {
    case "MATCH":
      return "match";
    case "TRAINING":
      return "entrainement";
    case "TOURNAMENT":
      return "tournoi";
    case "CULTURE":
      return "culture";
    case "BUREAU_MEETING":
      return "reunion_bureau";
    default:
      return "general";
  }
}

function toBackendEventType(type: AdminEvent["type"]) {
  switch (type) {
    case "match":
      return "MATCH";
    case "entrainement":
      return "TRAINING";
    case "tournoi":
      return "TOURNAMENT";
    case "culture":
      return "CULTURE";
    case "reunion_bureau":
      return "BUREAU_MEETING";
    default:
      return "GENERAL";
  }
}

function mapEventStatus(status: string): AdminEvent["statut"] {
  switch (status) {
    case "CANCELLED":
      return "annule";
    case "COMPLETED":
      return "termine";
    default:
      return "publie";
  }
}

function toBackendEventStatus(status: AdminEvent["statut"]) {
  switch (status) {
    case "annule":
      return "CANCELLED";
    case "termine":
      return "COMPLETED";
    default:
      return "PUBLISHED";
  }
}

function mapOrderStatus(status: string): AdminOrder["statut"] {
  switch (status) {
    case "CONFIRMED":
      return "confirme";
    case "SHIPPED":
      return "expedie";
    case "DELIVERED":
      return "livre";
    case "CANCELLED":
      return "annule";
    default:
      return "en_attente";
  }
}

function toBackendOrderStatus(status: AdminOrder["statut"]) {
  switch (status) {
    case "confirme":
      return "CONFIRMED";
    case "expedie":
      return "SHIPPED";
    case "livre":
      return "DELIVERED";
    case "annule":
      return "CANCELLED";
    default:
      return "PENDING";
  }
}

function mapPaymentStatus(status: string): AdminOrder["paymentStatus"] {
  switch (status) {
    case "PROCESSING":
      return "en_cours";
    case "SUCCEEDED":
      return "reussi";
    case "FAILED":
      return "echoue";
    default:
      return "en_attente";
  }
}

function toBackendContributionStatus(status: AdminMember["cotisation"]) {
  switch (status) {
    case "payee":
      return "PAID";
    case "impayee":
      return "UNPAID";
    default:
      return "PENDING";
  }
}

function mapLeisureStatus(isPublished: boolean): AdminLeisureVideo["statut"] {
  return isPublished ? "publie" : "masque";
}

function toBackendLeisureStatus(status: AdminLeisureVideo["statut"]) {
  return status === "publie";
}

function mapTeamStatus(status: string): AdminTeam["statut"] {
  return status === "INACTIVE" ? "inactif" : "actif";
}

function toBackendTeamStatus(status: AdminTeam["statut"]) {
  return status === "inactif" ? "INACTIVE" : "ACTIVE";
}

function mapMatchType(type: string): AdminMatch["type"] {
  switch (type) {
    case "LEAGUE":
      return "championnat";
    case "CUP":
      return "coupe";
    case "TOURNAMENT":
      return "tournoi";
    default:
      return "amical";
  }
}

function toBackendMatchType(type: AdminMatch["type"]) {
  switch (type) {
    case "championnat":
      return "LEAGUE";
    case "coupe":
      return "CUP";
    case "tournoi":
      return "TOURNAMENT";
    default:
      return "FRIENDLY";
  }
}

function mapMatchStatus(status: string): AdminMatch["statut"] {
  switch (status) {
    case "LIVE":
      return "en_cours";
    case "COMPLETED":
      return "termine";
    case "CANCELLED":
      return "annule";
    default:
      return "programme";
  }
}

function toBackendMatchStatus(status: AdminMatch["statut"]) {
  switch (status) {
    case "en_cours":
      return "LIVE";
    case "termine":
      return "COMPLETED";
    case "annule":
      return "CANCELLED";
    default:
      return "SCHEDULED";
  }
}

function mapSquadStatus(status: string): AdminSquad["status"] {
  switch (status) {
    case "FULL":
      return "complet";
    case "CLOSED":
      return "ferme";
    default:
      return "ouvert";
  }
}

function mapSquadApprovalStatus(status: string): AdminSquad["approvalStatus"] {
  switch (status) {
    case "APPROVED":
      return "approuve";
    case "REFUSED":
      return "refuse";
    default:
      return "en_attente";
  }
}

function toBackendSquadApprovalStatus(status: AdminSquad["approvalStatus"]) {
  switch (status) {
    case "approuve":
      return "APPROVED";
    case "refuse":
      return "REFUSED";
    default:
      return "PENDING";
  }
}

function mapAdminRequestStatus(status: string): AdminTeamJoinRequest["status"] {
  switch (status) {
    case "APPROVED":
      return "approuve";
    case "REFUSED":
      return "refuse";
    default:
      return "en_attente";
  }
}

function toBackendAdminRequestStatus(status: AdminTeamJoinRequest["status"]) {
  switch (status) {
    case "approuve":
      return "APPROVED";
    case "refuse":
      return "REFUSED";
    default:
      return "PENDING";
  }
}

function mapChallengeStatus(status: string): AdminChallenge["status"] {
  return status === "SUSPENDED" ? "suspendu" : "actif";
}

function toBackendChallengeStatus(status: AdminChallenge["status"]) {
  return status === "suspendu" ? "SUSPENDED" : "ACTIVE";
}

export async function fetchAdminDashboard(request: AdminRequest) {
  const data = await request<AdminDashboardData>("/admin/dashboard");

  return {
    ...data,
    pendingMembers: data.pendingMembers.map((member) => ({
      ...member,
      avatar: member.avatar ?? DEFAULT_AVATAR
    })),
    recentOrders: data.recentOrders.map((order) => ({
      ...order,
      statut: mapOrderStatus(order.statut)
    }))
  } as AdminDashboardData;
}

function mapMembersPage(
  data: PaginatedResult<{
    id: string;
    nom: string;
    prenom: string;
    email: string | null;
    matricule: string | null;
    telephone: string | null;
    departement: string | null;
    sport: string[];
    statut: string;
    dateInscription: string;
    dateValidation?: string;
    avatar: string | null;
    cotisation: string;
  }>
): PaginatedResult<AdminMember> {
  return {
    ...data,
    items: data.items.map((member) => ({
      ...member,
      avatar: member.avatar ?? DEFAULT_AVATAR,
      statut: mapMemberStatus(member.statut)
    })) as AdminMember[]
  };
}

export async function fetchMembersPage(
  request: AdminRequest,
  options?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: AdminMember["statut"];
  }
) {
  const data = await request<PaginatedResult<{
    id: string;
    nom: string;
    prenom: string;
    email: string | null;
    matricule: string | null;
    telephone: string | null;
    departement: string | null;
    sport: string[];
    statut: string;
    dateInscription: string;
    dateValidation?: string;
    avatar: string | null;
    cotisation: string;
  }>>(
    withQuery("/members", {
      page: options?.page ?? 1,
      limit: options?.limit ?? 100,
      search: options?.search,
      status: options?.status ? toBackendMemberStatus(options.status) : undefined
    })
  );

  return mapMembersPage(data);
}

export async function fetchMembers(
  request: AdminRequest,
  options?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: AdminMember["statut"];
  }
) {
  const hasExplicitPagination = options?.page !== undefined || options?.limit !== undefined;

  if (hasExplicitPagination) {
    const data = await fetchMembersPage(request, options);
    return data.items;
  }

  const firstPage = await fetchMembersPage(request, {
    ...options,
    page: 1,
    limit: 100
  });

  if (firstPage.totalPages <= 1) {
    return firstPage.items;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
      fetchMembersPage(request, {
        ...options,
        page: index + 2,
        limit: firstPage.limit
      })
    )
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.items);
}

export async function updateMemberStatus(request: AdminRequest, memberId: string, status: AdminMember["statut"]) {
  return request(`/members/${memberId}/status`, {
    method: "PATCH",
    body: {
      status: toBackendMemberStatus(status)
    }
  });
}

export async function updateMemberContributionStatus(request: AdminRequest, memberId: string, status: AdminMember["cotisation"]) {
  return request(`/members/${memberId}/contribution-status`, {
    method: "PATCH",
    body: {
      status: toBackendContributionStatus(status)
    }
  });
}

export async function updateMembersContributionStatus(request: AdminRequest, memberIds: string[], status: Extract<AdminMember["cotisation"], "payee" | "impayee">) {
  return request<{ updatedCount: number }>("/members/contribution-status/bulk", {
    method: "PATCH",
    body: {
      memberIds,
      status: toBackendContributionStatus(status)
    }
  });
}

export async function deleteMember(request: AdminRequest, memberId: string) {
  return request(`/members/${memberId}`, {
    method: "DELETE"
  });
}

export async function importMembersFromSpreadsheet(request: AdminRequest, file: File) {
  const formData = new FormData();
  formData.append("file", file);

  return request<AdminMemberImportReport>("/members/import", {
    method: "POST",
    body: formData
  });
}

export async function fetchDirections(request: AdminRequest) {
  return request<AdminDirection[]>("/members/directions");
}

export async function createDirection(request: AdminRequest, nom: string) {
  return request<AdminDirection>("/members/directions", {
    method: "POST",
    body: { nom }
  });
}

export async function updateDirection(request: AdminRequest, directionId: string, nom: string) {
  return request<AdminDirection>(`/members/directions/${directionId}`, {
    method: "PATCH",
    body: { nom }
  });
}

export async function deleteDirection(request: AdminRequest, directionId: string) {
  return request(`/members/directions/${directionId}`, {
    method: "DELETE"
  });
}

export async function fetchBureauMembers(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    nom: string;
    role: string;
    departement: string | null;
    email: string | null;
    telephone: string | null;
    avatar: string | null;
    ordre: number;
  }>>("/members/bureau");

  return data.map((member) => ({
    ...member,
    avatar: member.avatar ?? DEFAULT_AVATAR
  })) as AdminBureauMember[];
}

export async function createBureauMember(
  request: AdminRequest,
  member: Omit<AdminBureauMember, "id"> & { avatar?: string | null }
) {
  const data = await request<{
    id: string;
    nom: string;
    role: string;
    departement: string | null;
    email: string | null;
    telephone: string | null;
    avatar: string | null;
    ordre: number;
  }>("/members/bureau", {
    method: "POST",
    body: {
      nom: member.nom,
      role: member.role,
      departement: member.departement,
      email: member.email,
      telephone: member.telephone,
      avatar: member.avatar,
      ordre: member.ordre
    }
  });

  return {
    ...data,
    avatar: data.avatar ?? DEFAULT_AVATAR
  } as AdminBureauMember;
}

export async function updateBureauMember(
  request: AdminRequest,
  memberId: string,
  member: Partial<Omit<AdminBureauMember, "id">>
) {
  const data = await request<{
    id: string;
    nom: string;
    role: string;
    departement: string | null;
    email: string | null;
    telephone: string | null;
    avatar: string | null;
    ordre: number;
  }>(`/members/bureau/${memberId}`, {
    method: "PATCH",
    body: {
      ...(member.nom !== undefined ? { nom: member.nom } : {}),
      ...(member.role !== undefined ? { role: member.role } : {}),
      ...(member.departement !== undefined ? { departement: member.departement } : {}),
      ...(member.email !== undefined ? { email: member.email } : {}),
      ...(member.telephone !== undefined ? { telephone: member.telephone } : {}),
      ...(member.avatar !== undefined ? { avatar: member.avatar } : {}),
      ...(member.ordre !== undefined ? { ordre: member.ordre } : {})
    }
  });

  return {
    ...data,
    avatar: data.avatar ?? DEFAULT_AVATAR
  } as AdminBureauMember;
}

export async function deleteBureauMember(request: AdminRequest, memberId: string) {
  return request(`/members/bureau/${memberId}`, {
    method: "DELETE"
  });
}

export async function fetchNews(request: AdminRequest) {
  const data = await request<PaginatedResult<{
    id: string;
    titre: string;
    categorie: string;
    date: string;
    extrait: string;
    image: string | null;
    contenu: string;
    featured: boolean;
    statut: string;
  }>>(withQuery("/news", { page: 1, limit: 100 }));

  return data.items.map((article) => ({
    ...article,
    image: article.image ?? DEFAULT_IMAGE,
    statut: mapArticleStatus(article.statut)
  })) as AdminArticle[];
}

export async function createNews(request: AdminRequest, article: Omit<AdminArticle, "id" | "date">) {
  const data = await request<{
    id: string;
    titre: string;
    categorie: string;
    date: string;
    extrait: string;
    image: string | null;
    contenu: string;
    featured: boolean;
    statut: string;
  }>("/news", {
    method: "POST",
    body: {
      titre: article.titre,
      categorie: article.categorie,
      extrait: article.extrait,
      contenu: article.contenu,
      image: article.image,
      featured: article.featured,
      statut: toBackendArticleStatus(article.statut)
    }
  });

  return {
    ...data,
    image: data.image ?? DEFAULT_IMAGE,
    statut: mapArticleStatus(data.statut)
  } as AdminArticle;
}

export async function updateNews(request: AdminRequest, articleId: string, article: Partial<Omit<AdminArticle, "id" | "date">>) {
  const data = await request<{
    id: string;
    titre: string;
    categorie: string;
    date: string;
    extrait: string;
    image: string | null;
    contenu: string;
    featured: boolean;
    statut: string;
  }>(`/news/${articleId}`, {
    method: "PATCH",
    body: {
      ...(article.titre !== undefined ? { titre: article.titre } : {}),
      ...(article.categorie !== undefined ? { categorie: article.categorie } : {}),
      ...(article.extrait !== undefined ? { extrait: article.extrait } : {}),
      ...(article.contenu !== undefined ? { contenu: article.contenu } : {}),
      ...(article.image !== undefined ? { image: article.image } : {}),
      ...(article.featured !== undefined ? { featured: article.featured } : {}),
      ...(article.statut !== undefined ? { statut: toBackendArticleStatus(article.statut) } : {})
    }
  });

  return {
    ...data,
    image: data.image ?? DEFAULT_IMAGE,
    statut: mapArticleStatus(data.statut)
  } as AdminArticle;
}

export async function deleteNews(request: AdminRequest, articleId: string) {
  return request(`/news/${articleId}`, {
    method: "DELETE"
  });
}

export async function fetchEvents(request: AdminRequest) {
  const data = await request<PaginatedResult<{
    id: string;
    titre: string;
    type: string;
    date: string;
    heure: string;
    dateFin?: string | null;
    heureFin?: string | null;
    lieu: string;
    participants: number;
    maxParticipants: number;
    description: string;
    image: string | null;
    meetingUrl: string | null;
    meetingParticipantIds?: string[];
    statut: string;
  }>>(withQuery("/events", { page: 1, limit: 100 }));

  return data.items.map((event) => ({
    ...event,
    dateFin: event.dateFin ?? null,
    heureFin: event.heureFin ?? null,
    image: event.image,
    meetingParticipantIds: event.meetingParticipantIds ?? [],
    type: mapEventType(event.type),
    statut: mapEventStatus(event.statut)
  })) as AdminEvent[];
}

export async function createEvent(
  request: AdminRequest,
  event: Omit<AdminEvent, "id" | "participants" | "image"> & { image?: string }
) {
  const data = await request<{
    id: string;
    titre: string;
    type: string;
    date: string;
    heure: string;
    dateFin?: string | null;
    heureFin?: string | null;
    lieu: string;
    participants: number;
    maxParticipants: number;
    description: string;
    image: string | null;
    meetingUrl: string | null;
    meetingParticipantIds?: string[];
    statut: string;
  }>("/events", {
    method: "POST",
    body: {
      titre: event.titre,
      type: toBackendEventType(event.type),
      date: event.date,
      heure: event.heure,
      dateFin: event.dateFin,
      heureFin: event.heureFin,
      lieu: event.lieu,
      maxParticipants: event.maxParticipants,
      description: event.description,
      ...(event.meetingUrl !== undefined ? { meetingUrl: event.meetingUrl } : {}),
      ...(event.meetingParticipantIds !== undefined ? { participantIds: event.meetingParticipantIds } : {}),
      ...(event.image ? { image: event.image } : {}),
      statut: toBackendEventStatus(event.statut)
    }
  });

  return {
    ...data,
    dateFin: data.dateFin ?? null,
    heureFin: data.heureFin ?? null,
    image: data.image,
    meetingParticipantIds: data.meetingParticipantIds ?? [],
    type: mapEventType(data.type),
    statut: mapEventStatus(data.statut)
  } as AdminEvent;
}

export async function updateEvent(request: AdminRequest, eventId: string, event: Partial<Omit<AdminEvent, "id" | "participants">>) {
  const data = await request<{
    id: string;
    titre: string;
    type: string;
    date: string;
    heure: string;
    dateFin?: string | null;
    heureFin?: string | null;
    lieu: string;
    participants: number;
    maxParticipants: number;
    description: string;
    image: string | null;
    meetingUrl: string | null;
    meetingParticipantIds?: string[];
    statut: string;
  }>(`/events/${eventId}`, {
    method: "PATCH",
    body: {
      ...(event.titre !== undefined ? { titre: event.titre } : {}),
      ...(event.type !== undefined ? { type: toBackendEventType(event.type) } : {}),
      ...(event.date !== undefined ? { date: event.date } : {}),
      ...(event.heure !== undefined ? { heure: event.heure } : {}),
      ...(event.dateFin !== undefined ? { dateFin: event.dateFin } : {}),
      ...(event.heureFin !== undefined ? { heureFin: event.heureFin } : {}),
      ...(event.lieu !== undefined ? { lieu: event.lieu } : {}),
      ...(event.maxParticipants !== undefined ? { maxParticipants: event.maxParticipants } : {}),
      ...(event.description !== undefined ? { description: event.description } : {}),
      ...(event.image !== undefined ? { image: event.image } : {}),
      ...(event.meetingUrl !== undefined ? { meetingUrl: event.meetingUrl } : {}),
      ...(event.meetingParticipantIds !== undefined ? { participantIds: event.meetingParticipantIds } : {}),
      ...(event.statut !== undefined ? { statut: toBackendEventStatus(event.statut) } : {})
    }
  });

  return {
    ...data,
    dateFin: data.dateFin ?? null,
    heureFin: data.heureFin ?? null,
    image: data.image,
    meetingParticipantIds: data.meetingParticipantIds ?? [],
    type: mapEventType(data.type),
    statut: mapEventStatus(data.statut)
  } as AdminEvent;
}

export async function deleteEvent(request: AdminRequest, eventId: string) {
  return request(`/events/${eventId}`, {
    method: "DELETE"
  });
}

export async function fetchEventParticipants(request: AdminRequest, eventId: string) {
  return request<AdminEventParticipant[]>(`/events/${eventId}/participants`);
}

export async function fetchLeisureVideos(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    title: string;
    sourceUrl: string;
    embedUrl: string;
    provider: string;
    videoId: string;
    thumbnailUrl: string;
    isLive: boolean;
    isPublished: boolean;
    createdAt: string;
  }>>("/culture/leisure-videos/admin");

  return data.map((video) => ({
    ...video,
    statut: mapLeisureStatus(video.isPublished)
  })) as AdminLeisureVideo[];
}

export async function createLeisureVideo(
  request: AdminRequest,
  input: Pick<AdminLeisureVideo, "title" | "sourceUrl" | "statut">
) {
  const data = await request<{
    id: string;
    title: string;
    sourceUrl: string;
    embedUrl: string;
    provider: string;
    videoId: string;
    thumbnailUrl: string;
    isLive: boolean;
    isPublished: boolean;
    createdAt: string;
  }>("/culture/leisure-videos", {
    method: "POST",
    body: {
      title: input.title,
      sourceUrl: input.sourceUrl,
      isPublished: toBackendLeisureStatus(input.statut)
    }
  });

  return {
    ...data,
    statut: mapLeisureStatus(data.isPublished)
  } as AdminLeisureVideo;
}

export async function updateLeisureVideo(
  request: AdminRequest,
  videoId: string,
  input: Partial<Pick<AdminLeisureVideo, "title" | "sourceUrl" | "statut">>
) {
  const data = await request<{
    id: string;
    title: string;
    sourceUrl: string;
    embedUrl: string;
    provider: string;
    videoId: string;
    thumbnailUrl: string;
    isLive: boolean;
    isPublished: boolean;
    createdAt: string;
  }>(`/culture/leisure-videos/${videoId}`, {
    method: "PATCH",
    body: {
      ...(input.title !== undefined ? { title: input.title } : {}),
      ...(input.sourceUrl !== undefined ? { sourceUrl: input.sourceUrl } : {}),
      ...(input.statut !== undefined ? { isPublished: toBackendLeisureStatus(input.statut) } : {})
    }
  });

  return {
    ...data,
    statut: mapLeisureStatus(data.isPublished)
  } as AdminLeisureVideo;
}

export async function deleteLeisureVideo(request: AdminRequest, videoId: string) {
  return request(`/culture/leisure-videos/${videoId}`, {
    method: "DELETE"
  });
}

export async function fetchGallery(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    title: string;
    src: string;
    category: string;
    mediaType: "image" | "video";
  }>>("/culture/gallery");

  return data.map((item) => ({
    ...item,
    src: item.src || DEFAULT_IMAGE
  })) as AdminGalleryItem[];
}

export async function uploadAdminMedia(request: AdminRequest, files: File[], folder: string) {
  const uploadedAssets: UploadedMediaAsset[] = [];

  for (let index = 0; index < files.length; index += ADMIN_MEDIA_UPLOAD_BATCH_SIZE) {
    const formData = new FormData();
    files.slice(index, index + ADMIN_MEDIA_UPLOAD_BATCH_SIZE).forEach((file) => formData.append("files", file));
    formData.append("folder", folder);

    const batchAssets = await request<UploadedMediaAsset[]>("/media/upload", {
      method: "POST",
      body: formData
    });

    uploadedAssets.push(...batchAssets);
  }

  return uploadedAssets;
}

export async function createGalleryItems(
  request: AdminRequest,
  items: Array<Pick<AdminGalleryItem, "title" | "category" | "mediaType"> & { image: string; publicId?: string | null }>
) {
  const data = await request<Array<{
    id: string;
    title: string;
    src: string;
    category: string;
    mediaType: "image" | "video";
  }>>("/culture/gallery/batch", {
    method: "POST",
    body: {
      items
    }
  });

  return data.map((item) => ({
    ...item,
    src: item.src || DEFAULT_IMAGE
  })) as AdminGalleryItem[];
}

export async function deleteGalleryItem(request: AdminRequest, galleryItemId: string) {
  return request(`/culture/gallery/${galleryItemId}`, {
    method: "DELETE"
  });
}

export async function deleteGalleryItems(request: AdminRequest, galleryItemIds: string[]) {
  return request("/culture/gallery/batch", {
    method: "DELETE",
    body: {
      ids: galleryItemIds
    }
  });
}

export async function fetchClubTrophies(request: AdminRequest) {
  return request<Array<{
    id: string;
    titre: string;
    sport: string;
    annee: string;
    rang: string;
    image: string;
    imagePublicId: string | null;
    imageResourceType: string | null;
    ordre: number;
  }>>("/club/trophies");
}

export async function createClubTrophy(
  request: AdminRequest,
  trophy: Omit<AdminClubTrophy, "id">
) {
  return request<{
    id: string;
    titre: string;
    sport: string;
    annee: string;
    rang: string;
    image: string;
    imagePublicId: string | null;
    imageResourceType: string | null;
    ordre: number;
  }>("/club/trophies", {
    method: "POST",
    body: trophy
  });
}

export async function updateClubTrophy(
  request: AdminRequest,
  trophyId: string,
  trophy: Partial<Omit<AdminClubTrophy, "id">>
) {
  return request<{
    id: string;
    titre: string;
    sport: string;
    annee: string;
    rang: string;
    image: string;
    imagePublicId: string | null;
    imageResourceType: string | null;
    ordre: number;
  }>(`/club/trophies/${trophyId}`, {
    method: "PATCH",
    body: trophy
  });
}

export async function deleteClubTrophy(request: AdminRequest, trophyId: string) {
  return request(`/club/trophies/${trophyId}`, {
    method: "DELETE"
  });
}

export async function fetchClubPalmares(request: AdminRequest) {
  return request<Array<{
    id: string;
    annee: string;
    titre: string;
    sport: string;
    rang: string;
    ordre: number;
  }>>("/club/palmares");
}

export async function createClubPalmares(
  request: AdminRequest,
  item: Omit<AdminClubPalmares, "id">
) {
  return request<{
    id: string;
    annee: string;
    titre: string;
    sport: string;
    rang: string;
    ordre: number;
  }>("/club/palmares", {
    method: "POST",
    body: item
  });
}

export async function updateClubPalmares(
  request: AdminRequest,
  palmaresId: string,
  item: Partial<Omit<AdminClubPalmares, "id">>
) {
  return request<{
    id: string;
    annee: string;
    titre: string;
    sport: string;
    rang: string;
    ordre: number;
  }>(`/club/palmares/${palmaresId}`, {
    method: "PATCH",
    body: item
  });
}

export async function deleteClubPalmares(request: AdminRequest, palmaresId: string) {
  return request(`/club/palmares/${palmaresId}`, {
    method: "DELETE"
  });
}

export async function fetchProducts(request: AdminRequest) {
  const data = await request<PaginatedResult<{
    id: string;
    name: string;
    description: string;
    price: number;
    image: string | null;
    imagePublicId?: string | null;
    imageResourceType?: string | null;
    category: string;
    stock: number;
    badge?: string | null;
    isActive: boolean;
  }>>(withQuery("/products", { page: 1, limit: 100 }));

  return data.items.map((product) => ({
    ...product,
    image: product.image ?? DEFAULT_IMAGE
  })) as AdminProduct[];
}

export async function createProduct(request: AdminRequest, product: Omit<AdminProduct, "id" | "isActive"> & { isActive?: boolean }) {
  const data = await request<{
    id: string;
    name: string;
    description: string;
    price: number;
    image: string | null;
    imagePublicId?: string | null;
    imageResourceType?: string | null;
    category: string;
    stock: number;
    badge?: string | null;
    isActive: boolean;
  }>("/products", {
    method: "POST",
    body: {
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.image,
      imagePublicId: product.imagePublicId,
      imageResourceType: product.imageResourceType,
      category: product.category,
      stock: product.stock,
      badge: product.badge,
      isActive: product.isActive ?? true
    }
  });

  return {
    ...data,
    image: data.image ?? DEFAULT_IMAGE
  } as AdminProduct;
}

export async function updateProduct(request: AdminRequest, productId: string, product: Partial<AdminProduct>) {
  const data = await request<{
    id: string;
    name: string;
    description: string;
    price: number;
    image: string | null;
    imagePublicId?: string | null;
    imageResourceType?: string | null;
    category: string;
    stock: number;
    badge?: string | null;
    isActive: boolean;
  }>(`/products/${productId}`, {
    method: "PATCH",
    body: {
      ...(product.name !== undefined ? { name: product.name } : {}),
      ...(product.description !== undefined ? { description: product.description } : {}),
      ...(product.price !== undefined ? { price: product.price } : {}),
      ...(product.image !== undefined ? { image: product.image } : {}),
      ...(product.imagePublicId !== undefined ? { imagePublicId: product.imagePublicId } : {}),
      ...(product.imageResourceType !== undefined ? { imageResourceType: product.imageResourceType } : {}),
      ...(product.category !== undefined ? { category: product.category } : {}),
      ...(product.stock !== undefined ? { stock: product.stock } : {}),
      ...(product.badge !== undefined ? { badge: product.badge } : {}),
      ...(product.isActive !== undefined ? { isActive: product.isActive } : {})
    }
  });

  return {
    ...data,
    image: data.image ?? DEFAULT_IMAGE
  } as AdminProduct;
}

export async function deleteProduct(request: AdminRequest, productId: string) {
  return request(`/products/${productId}`, {
    method: "DELETE"
  });
}

export async function fetchOrders(request: AdminRequest) {
  const data = await request<PaginatedResult<{
    id: string;
    code: string;
    userId: string | null;
    memberName: string;
    memberEmail: string | null;
    status: string;
    paymentProvider: string | null;
    paymentStatus: string;
    paymentReference: string | null;
    paymentPaidAt: string | null;
    total: number;
    date: string;
    items: Array<{
      productId: string | null;
      name: string;
      quantity: number;
      unitPrice: number;
    }>;
  }>>(withQuery("/orders", { page: 1, limit: 100 }));

  return data.items.map((order) => ({
    id: order.id,
    code: order.code,
    userId: order.userId,
    membre: order.memberName,
    email: order.memberEmail,
    total: order.total,
    statut: mapOrderStatus(order.status),
    paymentProvider: order.paymentProvider,
    paymentStatus: mapPaymentStatus(order.paymentStatus),
    paymentReference: order.paymentReference,
    paymentPaidAt: order.paymentPaidAt,
    date: order.date,
    produits: order.items.map((item) => ({
      productId: item.productId,
      nom: item.name,
      quantite: item.quantity,
      prix: item.unitPrice
    }))
  })) as AdminOrder[];
}

export async function updateOrderStatus(request: AdminRequest, orderId: string, status: AdminOrder["statut"]) {
  const data = await request<{
    id: string;
    code: string;
    userId: string | null;
    memberName: string;
    memberEmail: string | null;
    status: string;
    paymentProvider: string | null;
    paymentStatus: string;
    paymentReference: string | null;
    paymentPaidAt: string | null;
    total: number;
    date: string;
    items: Array<{
      productId: string | null;
      name: string;
      quantity: number;
      unitPrice: number;
    }>;
  }>(`/orders/${orderId}/status`, {
    method: "PATCH",
    body: {
      status: toBackendOrderStatus(status)
    }
  });

  return {
    id: data.id,
    code: data.code,
    userId: data.userId,
    membre: data.memberName,
    email: data.memberEmail,
    total: data.total,
    statut: mapOrderStatus(data.status),
    paymentProvider: data.paymentProvider,
    paymentStatus: mapPaymentStatus(data.paymentStatus),
    paymentReference: data.paymentReference,
    paymentPaidAt: data.paymentPaidAt,
    date: data.date,
    produits: data.items.map((item) => ({
      productId: item.productId,
      nom: item.name,
      quantite: item.quantity,
      prix: item.unitPrice
    }))
  } as AdminOrder;
}

export async function fetchTeams(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    nom: string;
    sport: string;
    couleur: string;
    entraineur: string;
    statut: string;
    logo?: string | null;
    joueurs: Array<{
      id: string;
      nom: string;
      poste: string;
      numero: number;
      userId?: string | null;
      membreNom?: string | null;
      membreEmail?: string | null;
    }>;
  }>>("/teams");

  return data.map((team) => ({
    ...team,
    statut: mapTeamStatus(team.statut)
  })) as AdminTeam[];
}

export async function fetchTeamJoinRequests(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    status: string;
    createdAt: string;
    reviewedAt: string | null;
    team: {
      id: string;
      nom: string;
      sport: string;
    };
    member: {
      id: string;
      nom: string;
      email: string | null;
      telephone: string | null;
      matricule: string | null;
      departement: string | null;
      avatar: string | null;
    };
  }>>("/teams/join-requests");

  return data.map((item) => ({
    ...item,
    status: mapAdminRequestStatus(item.status),
    member: {
      ...item.member,
      avatar: item.member.avatar ?? DEFAULT_AVATAR
    }
  })) as AdminTeamJoinRequest[];
}

export async function reviewTeamJoinRequest(
  request: AdminRequest,
  requestId: string,
  status: Extract<AdminTeamJoinRequest["status"], "approuve" | "refuse">
) {
  const data = await request<{
    id: string;
    status: string;
    createdAt: string;
    reviewedAt: string | null;
    team: {
      id: string;
      nom: string;
      sport: string;
    };
    member: {
      id: string;
      nom: string;
      email: string | null;
      telephone: string | null;
      matricule: string | null;
      departement: string | null;
      avatar: string | null;
    };
  }>(`/teams/join-requests/${requestId}/review`, {
    method: "PATCH",
    body: {
      status: toBackendAdminRequestStatus(status)
    }
  });

  return {
    ...data,
    status: mapAdminRequestStatus(data.status),
    member: {
      ...data.member,
      avatar: data.member.avatar ?? DEFAULT_AVATAR
    }
  } as AdminTeamJoinRequest;
}

export async function createTeam(request: AdminRequest, team: Omit<AdminTeam, "id">) {
  const data = await request<{
    id: string;
    nom: string;
    sport: string;
    couleur: string;
    entraineur: string;
    statut: string;
    logo?: string | null;
    joueurs: Array<{
      id: string;
      nom: string;
      poste: string;
      numero: number;
      userId?: string | null;
      membreNom?: string | null;
      membreEmail?: string | null;
    }>;
  }>("/teams", {
    method: "POST",
    body: {
      nom: team.nom,
      sport: team.sport,
      couleur: team.couleur,
      entraineur: team.entraineur,
      statut: toBackendTeamStatus(team.statut),
      logo: team.logo,
      joueurs: team.joueurs.map((joueur) => ({
        nom: joueur.nom,
        poste: joueur.poste,
        numero: joueur.numero,
        userId: joueur.userId ?? ""
      }))
    }
  });

  return {
    ...data,
    statut: mapTeamStatus(data.statut)
  } as AdminTeam;
}

export async function updateTeam(request: AdminRequest, teamId: string, team: Partial<AdminTeam>) {
  const data = await request<{
    id: string;
    nom: string;
    sport: string;
    couleur: string;
    entraineur: string;
    statut: string;
    logo?: string | null;
    joueurs: Array<{
      id: string;
      nom: string;
      poste: string;
      numero: number;
      userId?: string | null;
      membreNom?: string | null;
      membreEmail?: string | null;
    }>;
  }>(`/teams/${teamId}`, {
    method: "PATCH",
    body: {
      ...(team.nom !== undefined ? { nom: team.nom } : {}),
      ...(team.sport !== undefined ? { sport: team.sport } : {}),
      ...(team.couleur !== undefined ? { couleur: team.couleur } : {}),
      ...(team.entraineur !== undefined ? { entraineur: team.entraineur } : {}),
      ...(team.statut !== undefined ? { statut: toBackendTeamStatus(team.statut) } : {}),
      ...(team.logo !== undefined ? { logo: team.logo } : {}),
      ...(team.joueurs !== undefined
        ? {
            joueurs: team.joueurs.map((joueur) => ({
              nom: joueur.nom,
              poste: joueur.poste,
              numero: joueur.numero,
              userId: joueur.userId ?? ""
            }))
          }
        : {})
    }
  });

  return {
    ...data,
    statut: mapTeamStatus(data.statut)
  } as AdminTeam;
}

export async function deleteTeam(request: AdminRequest, teamId: string) {
  return request(`/teams/${teamId}`, {
    method: "DELETE"
  });
}

export async function deleteAdminSquad(request: AdminRequest, squadId: string) {
  return request(`/squads/${squadId}`, {
    method: "DELETE"
  });
}

export async function fetchLiveStreams(request: AdminRequest) {
  return request<AdminLiveStream[]>("/live-streams");
}

export async function startLiveStream(
  request: AdminRequest,
  input: {
    title: string;
    sourceUrl: string;
    badgeLabel?: string;
  }
) {
  return request<AdminLiveStream>("/live-streams", {
    method: "POST",
    body: input
  });
}

export async function stopLiveStream(request: AdminRequest, liveStreamId: string) {
  return request<AdminLiveStream>(`/live-streams/${liveStreamId}/stop`, {
    method: "POST",
    body: {}
  });
}

export async function deleteLiveStream(request: AdminRequest, liveStreamId: string) {
  return request(`/live-streams/${liveStreamId}`, {
    method: "DELETE",
    body: {}
  });
}

export async function fetchAdminSquads(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    color: string;
    maxMembers: number;
    status: string;
    approvalStatus: string;
    createdAt: string;
    reviewedAt: string | null;
    creator: {
      id: string;
      name: string;
      email: string;
      avatar: string | null;
    };
    members: Array<{
      id: string;
      name: string;
      avatar: string | null;
      online: boolean;
    }>;
  }>>("/squads/admin");

  return data.map((squad) => ({
    ...squad,
    status: mapSquadStatus(squad.status),
    approvalStatus: mapSquadApprovalStatus(squad.approvalStatus)
  })) as AdminSquad[];
}

export async function reviewAdminSquad(request: AdminRequest, squadId: string, approvalStatus: AdminSquad["approvalStatus"]) {
  const data = await request<{
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    color: string;
    maxMembers: number;
    status: string;
    approvalStatus: string;
    createdAt: string;
    reviewedAt: string | null;
    creator: {
      id: string;
      name: string;
      email: string;
      avatar: string | null;
    };
    members: Array<{
      id: string;
      name: string;
      avatar: string | null;
      online: boolean;
    }>;
  }>(`/squads/${squadId}/review`, {
    method: "PATCH",
    body: {
      approvalStatus: toBackendSquadApprovalStatus(approvalStatus)
    }
  });

  return {
    ...data,
    status: mapSquadStatus(data.status),
    approvalStatus: mapSquadApprovalStatus(data.approvalStatus)
  } as AdminSquad;
}

export async function fetchMatches(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    equipeDomicile: string;
    equipeExterieur: string;
    sport: string;
    date: string;
    heure: string;
    lieu: string;
    type: string;
    statut: string;
    scoreDomicile?: number | null;
    scoreExterieur?: number | null;
  }>>("/matches");

  return data.map((match) => ({
    ...match,
    type: mapMatchType(match.type),
    statut: mapMatchStatus(match.statut)
  })) as AdminMatch[];
}

export async function createMatch(request: AdminRequest, match: Omit<AdminMatch, "id">) {
  const data = await request<{
    id: string;
    equipeDomicile: string;
    equipeExterieur: string;
    sport: string;
    date: string;
    heure: string;
    lieu: string;
    type: string;
    statut: string;
    scoreDomicile?: number | null;
    scoreExterieur?: number | null;
  }>("/matches", {
    method: "POST",
    body: {
      equipeDomicile: match.equipeDomicile,
      equipeExterieur: match.equipeExterieur,
      sport: match.sport,
      date: match.date,
      heure: match.heure,
      lieu: match.lieu,
      type: toBackendMatchType(match.type),
      statut: toBackendMatchStatus(match.statut),
      scoreDomicile: match.scoreDomicile ?? undefined,
      scoreExterieur: match.scoreExterieur ?? undefined
    }
  });

  return {
    ...data,
    type: mapMatchType(data.type),
    statut: mapMatchStatus(data.statut)
  } as AdminMatch;
}

export async function updateMatch(request: AdminRequest, matchId: string, match: Partial<AdminMatch>) {
  const data = await request<{
    id: string;
    equipeDomicile: string;
    equipeExterieur: string;
    sport: string;
    date: string;
    heure: string;
    lieu: string;
    type: string;
    statut: string;
    scoreDomicile?: number | null;
    scoreExterieur?: number | null;
  }>(`/matches/${matchId}`, {
    method: "PATCH",
    body: {
      ...(match.equipeDomicile !== undefined ? { equipeDomicile: match.equipeDomicile } : {}),
      ...(match.equipeExterieur !== undefined ? { equipeExterieur: match.equipeExterieur } : {}),
      ...(match.sport !== undefined ? { sport: match.sport } : {}),
      ...(match.date !== undefined ? { date: match.date } : {}),
      ...(match.heure !== undefined ? { heure: match.heure } : {}),
      ...(match.lieu !== undefined ? { lieu: match.lieu } : {}),
      ...(match.type !== undefined ? { type: toBackendMatchType(match.type) } : {}),
      ...(match.statut !== undefined ? { statut: toBackendMatchStatus(match.statut) } : {}),
      ...(match.scoreDomicile !== undefined ? { scoreDomicile: match.scoreDomicile } : {}),
      ...(match.scoreExterieur !== undefined ? { scoreExterieur: match.scoreExterieur } : {})
    }
  });

  return {
    ...data,
    type: mapMatchType(data.type),
    statut: mapMatchStatus(data.statut)
  } as AdminMatch;
}

export async function deleteMatch(request: AdminRequest, matchId: string) {
  return request(`/matches/${matchId}`, {
    method: "DELETE"
  });
}

export async function fetchTrainings(request: AdminRequest) {
  return request<AdminTraining[]>("/trainings");
}

export async function createTraining(request: AdminRequest, training: Omit<AdminTraining, "id" | "equipeNom" | "sport">) {
  return request<AdminTraining>("/trainings", {
    method: "POST",
    body: {
      equipeId: training.equipeId,
      jour: training.jour,
      heure: training.heure,
      lieu: training.lieu
    }
  });
}

export async function updateTraining(
  request: AdminRequest,
  trainingId: string,
  training: Partial<Omit<AdminTraining, "id" | "equipeNom" | "sport">>
) {
  return request<AdminTraining>(`/trainings/${trainingId}`, {
    method: "PATCH",
    body: {
      ...(training.equipeId !== undefined ? { equipeId: training.equipeId } : {}),
      ...(training.jour !== undefined ? { jour: training.jour } : {}),
      ...(training.heure !== undefined ? { heure: training.heure } : {}),
      ...(training.lieu !== undefined ? { lieu: training.lieu } : {})
    }
  });
}

export async function deleteTraining(request: AdminRequest, trainingId: string) {
  return request(`/trainings/${trainingId}`, {
    method: "DELETE"
  });
}

export async function fetchAdminChallenges(request: AdminRequest) {
  const data = await request<Array<{
    id: string;
    title: string;
    description: string;
    sport: string;
    icon: string;
    color: string;
    bgColor: string;
    image: string | null;
    target: number;
    unit: string;
    deadline: string;
    participants: number;
    reward: string;
    creatorName: string | null;
    createdBySquadLeader: boolean;
    status: string;
    createdAt: string;
    leaderboard: Array<{
      rank: number;
      name: string;
      avatar: string | null;
      score: number;
      badge?: string;
    }>;
  }>>("/challenges/admin");

  return data.map((challenge) => ({
    ...challenge,
    status: mapChallengeStatus(challenge.status)
  })) as AdminChallenge[];
}

export async function updateAdminChallengeStatus(request: AdminRequest, challengeId: string, status: AdminChallenge["status"]) {
  const data = await request<{
    id: string;
    title: string;
    description: string;
    sport: string;
    icon: string;
    color: string;
    bgColor: string;
    image: string | null;
    target: number;
    unit: string;
    deadline: string;
    participants: number;
    reward: string;
    creatorName: string | null;
    createdBySquadLeader: boolean;
    status: string;
    createdAt: string;
    leaderboard: Array<{
      rank: number;
      name: string;
      avatar: string | null;
      score: number;
      badge?: string;
    }>;
  }>(`/challenges/${challengeId}/status`, {
    method: "PATCH",
    body: {
      status: toBackendChallengeStatus(status)
    }
  });

  return {
    ...data,
    status: mapChallengeStatus(data.status)
  } as AdminChallenge;
}

export async function deleteAdminChallenge(request: AdminRequest, challengeId: string) {
  return request(`/challenges/${challengeId}`, {
    method: "DELETE"
  });
}
